import type { ErrorEvent } from "@sentry/nextjs"

// Asset/probe paths (cart.js, browsing_context_suggestions.json, .well-known,
// *.png, ...) route into the [appId] segment and, before the fetchAppById
// validation guard, fired `get-by-app-id/<file>` → upstream 400/422 (Sentry
// 76K/76M/76J/6KT). The by-page route also fetched `get-by-app-id/undefined`
// when the client posted an undefined appId (Sentry 7J2). The validation guard
// now prevents these at the source; this filter drops any residual/in-flight
// noise so those issues don't re-accrue. Genuine errors for valid appIds (e.g.
// a real 5xx) are preserved.
//
// Shared by sentry.server.config.ts and sentry.edge.config.ts so the server
// and edge runtimes filter identically (the [appId] home page is edge).

const PROBE_ENDPOINT =
  /(get-by-app-id|get-integrations-by-app-id|theme-options)\//
const PROBE_STATUS = /\b(400|404|422)\b/
const PROBE_TARGET =
  /(\.json|\.js|\.png|\.ico|\.txt|\.xml|\.map|\.well-known|favicon|apple-touch-icon|browsing_context_suggestions|undefined|null)/i

export const isExpectedProbeEvent = (event: ErrorEvent): boolean => {
  const message = event.exception?.values?.[0]?.value ?? ""

  if (
    PROBE_ENDPOINT.test(message) &&
    PROBE_STATUS.test(message) &&
    PROBE_TARGET.test(message)
  ) {
    return true
  }

  // Residual events from the [appId] layout preflight guard for an expected
  // probe rejection (the guard now notFound()s these, but drop any that slip
  // through tagged as a 400/404/422 preflight failure).
  if (
    event.tags?.context === "app_preflight_validation_failed" &&
    PROBE_STATUS.test(message)
  ) {
    return true
  }

  return false
}

export const dropExpectedProbeEvents = (event: ErrorEvent): ErrorEvent | null =>
  isExpectedProbeEvent(event) ? null : event

// WebKit storage eviction rejects idb's internal unobserved promises with
// "UnknownError: Database deleted by request of the user". The
// unhandledrejection backstop in ShopifyCacheIDB recovers and reports one
// tagged `idb_evicted` event, but its preventDefault() cannot suppress
// Sentry's own GlobalHandlers capture — instrumentation-client.ts drops the
// raw duplicates with this predicate.
//
// Sentry renders the rejected DOMException two ways:
// - No stack (the observed WebKit shape, Sentry issue 7P7): exception type
//   "Error" with the DOMException name prefixed onto the value —
//   `UnknownError: Database deleted by request of the user`.
// - With a stack: exception type "UnknownError" with the bare message as the
//   value.
//
// The deliberate tagged `idb_evicted` event must keep flowing. It is doubly
// protected: its mechanism is `generic`/handled (not `onunhandledrejection`),
// and its message (`IDB evicted (Database deleted)`) neither starts with
// `UnknownError:` nor carries the `UnknownError` exception type.
export const isIdbEvictionDuplicateEvent = (event: ErrorEvent): boolean => {
  const firstException = event.exception?.values?.[0]
  if (firstException?.mechanism?.type !== "onunhandledrejection") {
    return false
  }
  const { type } = firstException
  const value = firstException.value ?? ""
  return (
    (type === "UnknownError" && /database deleted/i.test(value)) ||
    (type === "Error" && /^UnknownError: Database deleted/i.test(value))
  )
}
